import SummaryCalculator from '../../../../common/summaryCalculator';
import { getCalculationExtraInfo } from '../../../../common/summaryCalculator/calculation-extra-info';

import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  useDeclarationsByOrderIdQuery,
  useGetBankCommissionsByOrderIdQuery,
  useGetBrokerDataByCodeQuery,
  useGetOrderSummaryByIdQuery,
  useGetOrderSummaryInvoicesByIdQuery,
  useGetExtraServicesByOrderIdQuery,
  useGetRouteByOrderIdQuery,
  useInvoicesByOrderIdQuery,
  useOrderByIdQuery,
  useSummaryByOrderIdQuery,
} from "../../../../../api/calc";
import isEmpty from "lodash/isEmpty";
import sortBy from "lodash/sortBy";


const Summary = ({ orderId, noLimeColor }: CalculationSummaryProps) => {
  const { t } = useTranslation()

  const skip = !orderId

  const { data: orderData } = useOrderByIdQuery(orderId, { skip })
  const { data: packagingSummary } = useSummaryByOrderIdQuery(orderId, { skip })
  const { data: summaryInfo } = useGetOrderSummaryByIdQuery(orderId, { skip })
  const { data: summaryInvoices } = useGetOrderSummaryInvoicesByIdQuery(orderId, { skip })
  const { data: extraServices } = useGetExtraServicesByOrderIdQuery(orderId, { skip })
  const { data: summaryDelivery } = useGetRouteByOrderIdQuery(orderId, { skip })
  const { data: declarations } = useDeclarationsByOrderIdQuery(orderId, { skip })
  const { data: bankCommissions } = useGetBankCommissionsByOrderIdQuery({ orderId }, { skip })
  const { data: summaryCertification } = useGetBrokerDataByCodeQuery(orderId, { skip })

  const invoices = useInvoicesByOrderIdQuery(orderId, { skip })?.data
  const sortedInvoices = sortBy(invoices, 'id')

  const summaryOrder = useMemo(() => {
    const packaging = packagingSummary ?? summaryInfo?.packaging
    if (!packaging) {
      return undefined
    }

    return {
      ...packaging,
      ...summaryInfo?.summary,
      containers_user: !isEmpty(packaging?.containers_manual)
        ? packaging?.containers_manual
        : packaging?.containers,
    }
  }, [ packagingSummary, summaryInfo ])

  const order = useMemo(() => (
    orderData ? {
      ...orderData,
      bankCommissions: bankCommissions ?? orderData.bankCommissions,
      userExtraServices: extraServices ?? orderData.userExtraServices,
    } : undefined
  ), [ orderData, bankCommissions, extraServices ])

  const delivery = useMemo((): Route | undefined => {
    if (summaryDelivery) {
      return summaryDelivery
    }
    const fromSummary = summaryInfo?.delivery
    if (fromSummary && 'freights' in fromSummary) {
      return fromSummary
    }
    return undefined
  }, [ summaryDelivery, summaryInfo ])

  const calculationData: CalculationExtraInfoData = {
    order,
    packagingSummary,
    summaryInfo,
    summaryInvoices,
    invoices: sortedInvoices,
    route: delivery,
    declarations,
    bankCommissions,
    certification: summaryCertification,
    extraServices,
  }

  return (
    <SummaryCalculator
      noInfo
      calculation={{
        order,
        invoices: sortedInvoices,
        summaryOrder,
        summaryDelivery: delivery,
        summaryCustoms: summaryInfo?.customs,
        summaryCertification,
        summaryCompleted: summaryInfo?.completed,
        declarations,
        extraInfo: getCalculationExtraInfo({
          calculation: calculationData,
          translate: t,
        }),
      }}
      noLimeColor={noLimeColor}
    />
  )
}

export default Summary
